import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CreditContract, DaDataResponse, OCC_ENDPOINTS, RawCompanyData, UserCompany } from '@company/const';
import { HTTP_CONTEXT_ENDPOINT } from '@core/const';
import { OccEndpointsService } from '@spartacus/core';
import { UserJwtAuthority } from '@user/const';
import { JwtUserRoleService } from '@user/data-access';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class OccCompanyService {
  constructor(
    private http: HttpClient,
    private occEndpointsService: OccEndpointsService,
    private jwtUserRoleService: JwtUserRoleService,
  ) {}

  searchCompany(query: string): Observable<DaDataResponse[]> {
    const url = this.occEndpointsService.buildUrl(OCC_ENDPOINTS.searchCompany, {
      queryParams: { partOfVatIdOrDebtorOgrn: query, fields: 'FULL' },
    });

    return this.http
      .get<{ b2bUnits: DaDataResponse[] }>(url, {
        context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, OCC_ENDPOINTS.searchCompany),
      })
      .pipe(map(({ b2bUnits }) => b2bUnits));
  }

  addCompany(data: RawCompanyData): Observable<string> {
    const url = this.occEndpointsService.buildUrl(OCC_ENDPOINTS.userCompanies);

    return this.http
      .post<UserCompany>(url, data, {
        context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, OCC_ENDPOINTS.userCompanies),
      })
      .pipe(map((res) => res.uid));
  }

  removeCompany(companyId: string): Observable<unknown> {
    const url = this.occEndpointsService.buildUrl(OCC_ENDPOINTS.removeCompany, {
      urlParams: { companyId },
    });

    return this.http.delete(url, {
      context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, OCC_ENDPOINTS.removeCompany),
    });
  }

  loadUserCompanies(userId: string): Observable<UserCompany[]> {
    const url = this.occEndpointsService.buildUrl(OCC_ENDPOINTS.userCompanies, {
      urlParams: { userId },
    });

    return this.http
      .get<{ customerOrganizations: UserCompany[] }>(url, {
        context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, OCC_ENDPOINTS.userCompanies),
      })
      .pipe(
        map(({ customerOrganizations }) => customerOrganizations || []),
        switchMap((companies) => this.enrichWithCreditContracts(companies)),
      );
  }

  loadCreditContracts(organizationUid: string): Observable<CreditContract[]> {
    const url = this.occEndpointsService.buildUrl(OCC_ENDPOINTS.creditContracts, {
      urlParams: { organizationUid },
    });

    return this.http
      .get<{ creditContracts: CreditContract[] }>(url, {
        context: new HttpContext().set(HTTP_CONTEXT_ENDPOINT, OCC_ENDPOINTS.creditContracts),
      })
      .pipe(map(({ creditContracts }) => creditContracts || []));
  }

  private enrichWithCreditContracts(companies: UserCompany[]): Observable<UserCompany[]> {
    if (!companies.length) {
      return of([]);
    }

    return this.jwtUserRoleService.hasAuthority(UserJwtAuthority.SdkManager).pipe(
      take(1),
      switchMap((isManager) => {
        if (!isManager) {
          return of(companies);
        }

        return forkJoin(
          companies.map((company) =>
            this.loadCreditContracts(company.uid).pipe(
              catchError(() => of([] as CreditContract[])),
              map((creditContracts) => ({ ...company, creditContracts })),
            ),
          ),
        );
      }),
    );
  }
}
